<template>
  <div
    v-if="mutableAnalysisRequest.sample"
    class="coupon-cell"
  >
    <div v-if="sampleAttribute === 'sampleComment'">
      <VTextarea
        v-model="mutableAnalysisRequest.sample.comment"
        :readonly="isSummaryView"
        hideDetails
        dense
        outlined
        :rules="{ max: 50 }"
        @input="updateCouponSample('comment', $event, mutableAnalysisRequest, mutableAnalysisRequest.sample.id)"
      />
    </div>
    <div v-else-if="sampleAttribute === 'labId'">
      <VTextField
        v-model="mutableAnalysisRequest.sample.labId"
        :readonly="isSummaryView"
        hideDetails
        dense
        outlined
        color="isSummaryView ? #000 : #2E85A0"
        @input="updateCouponSample(sampleAttribute, $event, mutableAnalysisRequest, mutableAnalysisRequest.sample.id)"
      />
    </div>
    <div v-else-if="sampleAttribute === 'identification'">
      <p
        style="color:black"
        class="mb-0"
      >
        {{ mutableAnalysisRequest.sample.identification }}
      </p>
    </div>
    <div v-else-if="sampleAttribute === 'collectedBy'">
      {{ `${mutableAnalysisRequest.sample.collectedBy.firstName} ${mutableAnalysisRequest.sample.collectedBy.lastName}` }}
    </div>
    <div v-else-if="sampleAttribute === 'installedDate'">
      <VDatePicker
        :value="mutableAnalysisRequest.sample.installedDate"
        :viewonly="isSummaryView"
        hideDetails
        dense
        @input="updateCouponSample(sampleAttribute, moment($event).startOf('day'), mutableAnalysisRequest, mutableAnalysisRequest.sample.id)"
      />
    </div>
    <div v-else-if="sampleAttribute === 'collectedDate'">
      <VDatePicker
        :value="mutableAnalysisRequest.sample.collectedDate"
        :viewonly="isSummaryView"
        hideDetails
        dense
        @input="updateCouponSample(sampleAttribute, moment($event).startOf('day'), mutableAnalysisRequest, mutableAnalysisRequest.sample.id)"
      />
    </div>
    <div v-else-if="sampleAttribute === 'receivedDate'">
      <VDateTimePicker
        v-model="mutableAnalysisRequest.sample.receivedDate"
        :format="'L LT'"
        :readonly="isSummaryView"
        :locale="datePickerLocales[$store.getters.lang]"
        hideDetails
        dense
        @input="updateCouponSample(sampleAttribute, moment($event), mutableAnalysisRequest, mutableAnalysisRequest.sample.id)"
      />
    </div>
    <div v-else-if="sampleAttribute === 'applicableNorms'">
      <VSelect
        v-model="mutableAnalysisRequest[sampleAttribute]"
        :items="standardFamilyList"
        :disabled="isSummaryView"
        outlined
        hideDetails
        dense
        @input="updateAnalysisRequest(sampleAttribute, $event, mutableAnalysisRequest, null)"
      />
    </div>
    <div v-else-if="sampleAttribute === 'coupon'">
      <VSelect
        v-model="mutableAnalysisRequest.sample.coupon.id"
        :items="couponList"
        :disabled="isSummaryView"
        itemText="name"
        itemValue="id"
        outlined
        hideDetails
        dense
        @input="updateCouponSample(sampleAttribute, getCoupon($event), mutableAnalysisRequest, mutableAnalysisRequest.sample.id)"
      />
    </div>
    <div v-else-if="sampleAttribute === 'componentType'">
      {{ mutableAnalysisRequest.component.type.name }}
    </div>
    <div v-else-if="sampleAttribute === 'code'">
      <VTextField
        v-model="mutableAnalysisRequest.sample.code"
        :readonly="isSummaryView"
        hideDetails
        outlined
        dense
        @input="updateCouponSample(sampleAttribute, $event, mutableAnalysisRequest, mutableAnalysisRequest.sample.id)"
      />
    </div>
    <div v-else-if="sampleAttribute === 'initialWeight'">
      <VTextField
        v-model="mutableAnalysisRequest.sample.initialWeight"
        outlined
        type="number"
        label="g"
        :rules="{required: true}"
        :filled="isSummaryView"
        :readonly="isSummaryView"
        hideDetails
        dense
        @input="updateCouponSample(sampleAttribute, $event, mutableAnalysisRequest, mutableAnalysisRequest.sample.id)"
      />
    </div>
    <div v-else-if="sampleAttribute === 'finalWeight'">
      <VTextField
        v-model="mutableAnalysisRequest.sample.finalWeight"
        type="number"
        label="g"
        :rules="{required: true}"
        outlined
        :filled="isSummaryView"
        :readonly="isSummaryView"
        hideDetails
        dense
        @input="updateCouponSample(sampleAttribute, $event, mutableAnalysisRequest, mutableAnalysisRequest.sample.id)"
      />
    </div>
    <div v-else-if="sampleAttribute === 'daysInstalled'">
      <VTextField
        :value="daysInstalled"
        outlined
        label="j"
        type="number"
        filled
        readonly
        hideDetails
        dense
      />
    </div>
    <div v-else-if="sampleAttribute === 'density'">
      <VTextField
        :value="mutableAnalysisRequest.sample.coupon.density"
        label="g/cm^3"
        type="number"
        outlined
        filled
        readonly
        hideDetails
        dense
      />
    </div>
    <div v-else-if="sampleAttribute === 'surface'">
      <VTextField
        :value="mutableAnalysisRequest.sample.coupon.surface"
        label="po^2"
        type="number"
        outlined
        filled
        readonly
        hideDetails
        dense
      />
    </div>
    <div v-else-if="sampleAttribute === 'weightLost'">
      <VTextField
        :value="weightLost"
        label="mg"
        type="number"
        outlined
        filled
        readonly
        hideDetails
        dense
      />
    </div>
    <div v-else-if="sampleAttribute === 'corrosionRate'">
      <VTextField
        :value="corrosionRate"
        :label="$t('forms.labels.mpy')"
        type="number"
        outlined
        filled
        readonly
        hideDetails
        dense
      />
      <div
        v-if="corrosionRateClass && corrosionRateClass.length"
        class="corrosion-bar norm-color"
        :class="corrosionRateClass"
      >
        {{ $t(`labWorkOrderTable.corrosionLvl.${corrosionRateClass}`) }}
      </div>
    </div>
    <div v-else-if="sampleAttribute === 'treatment'">
      <VTextarea
        v-model="mutableAnalysisRequest.treatment"
        outlined
        :readonly="isSummaryView"
        hideDetails
        dense
        @input="updateAnalysisRequest('treatment', $event, mutableAnalysisRequest, null)"
      />
    </div>
    <div v-else-if="sampleAttribute === 'pictureBeforeCleaning'">
      <SamplePicture
        v-if="isSummaryView"
        :imgId="mutableAnalysisRequest.sample.pictureBeforeCleaning && mutableAnalysisRequest.sample.pictureBeforeCleaning.id"
      />
      <VFile
        v-else
        v-model="data.pictureBeforeCleaning"
        :isLoading="isLoading"
        :showSize="false"
        isEdition
        hideDetails
        dense
        appendOuterIcon="mdi-eye"
        @input="onFileChange(mutableAnalysisRequest, mutableAnalysisRequest.sample.id, 'pictureBeforeCleaning', $event)"
        @click:clear="onClearClick('pictureBeforeCleaning')"
      />
    </div>
    <div v-else-if="sampleAttribute === 'pictureAfterCleaning'">
      <SamplePicture
        v-if="isSummaryView"
        :imgId="mutableAnalysisRequest.sample.pictureAfterCleaning && mutableAnalysisRequest.sample.pictureAfterCleaning.id"
      />
      <VFile
        v-else
        v-model="data.pictureAfterCleaning"
        :isLoading="isLoading"
        :showSize="false"
        isEdition
        hideDetails
        dense
        appendOuterIcon="mdi-eye"
        @input="onFileChange(mutableAnalysisRequest, mutableAnalysisRequest.sample.id, 'pictureAfterCleaning', $event)"
        @click:clear="onClearClick('pictureAfterCleaning')"
      />
    </div>
    <div v-else>
      <VTextField
        v-model="mutableAnalysisRequest[sampleAttribute]"
        outlined
        :readonly="isSummaryView"
        hideDetails
        dense
        @input="updateAnalysisRequest(sampleAttribute, $event, mutableAnalysisRequest, null)"
      />
    </div>
  </div>
</template>

<script>
import moment from 'moment-timezone';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField';
import VTextarea from '@/components/shared/widgets/forms/VuetifyInputs/VTextarea.vue';
import VDatePicker from '@/components/shared/widgets/forms/VuetifyInputs/VDatePicker';
import VDateTimePicker from '@/components/shared/widgets/forms/VuetifyInputs/VDateTimePicker';
import VFile from '@/components/shared/widgets/forms/VuetifyInputs/VFile';
import SamplePicture from '@/views/workOrders/summary/SamplePicture';
import FormFilesMixin from '@/mixins/FormFilesMixin';
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect';
import _datePickerLocales from '@/locales/date-picker-locales';

export default {
    components: {
        VDateTimePicker,
        SamplePicture,
        VFile,
        VTextField,
        VDatePicker,
        VSelect,
        VTextarea,
    },
    mixins: [
        FormFilesMixin,
    ],
    props: {
        analysisRequest: {
            type: Object,
            default: () => ({}),
        },
        sampleAttribute: {
            type: String,
            default: '',
        },
        updateAnalysisRequest: {
            type: Function,
            default: () => {},
        },
        updateCouponSample: {
            type: Function,
            default: () => {},
        },
        updateSampleFile: {
            type: Function,
            default: () => {},
        },
        isSummaryView: {
            type: Boolean,
            default: false,
        },
        updatSample: {
            type: Function,
            default: () => {},
        },
        standardFamilyList: {
            type: Array,
            default: () => ([]),
        },
        analysisRequestStandards: {
            type: Array,
            default: () => ([]),
        },
        sampleId: {
            type: String,
            required: true,
        },
        couponList: {
            type: Array,
            default: () => ([]),
        },
    },
    data() {
        return {
            isLoading: false,
            mutableAnalysisRequest: {},
            data: {},
            fileFields: ['pictureBeforeCleaning', 'pictureAfterCleaning'],
        };
    },
    computed: {
        daysInstalled() {
            //  Collected date - Installed date
            const { sample, site } = this.mutableAnalysisRequest;
            const collectedDate = moment(sample.collectedDate).tz(site.tz).startOf('day');
            const installedDate = moment(sample.installedDate).tz(site.tz).startOf('day');
            const daysDiff = collectedDate.diff(installedDate, 'days');

            return daysDiff || 0;
        },
        weightLost() {
            // Initial weight - Final weight
            const { sample } = this.mutableAnalysisRequest;
            const result = sample.initialWeight * 1000 - sample.finalWeight * 1000;
            return result.toFixed(1) || 0;
        },
        corrosionRate() {
            // (Perte de masse (mg) * 22.3)/(densité (g/cm^3) * surface (po^2) * temps (j)
            const { sample } = this.mutableAnalysisRequest;
            const result = (this.weightLost * 22.3) / (sample.coupon.density * sample.coupon.surface * this.daysInstalled);
            return result.toFixed(2) || 0;
        },
        corrosionRateClass() {
            const sample = this.analysisRequest.samples.find(s => s.id === this.sampleId);
            const { excellent, veryGood, good, fair, poor, severe } = sample?.coupon?.norm || {};
            if (this.corrosionRate > excellent && this.corrosionRate <= veryGood) {
                return 'excellent';
            }
            if (this.corrosionRate > veryGood && this.corrosionRate <= good) {
                return 'veryGood';
            }
            if (this.corrosionRate > good && this.corrosionRate <= fair) {
                return 'good';
            }
            if (this.corrosionRate > fair && this.corrosionRate <= poor) {
                return 'fair';
            }
            if (this.corrosionRate > poor && this.corrosionRate <= severe) {
                return 'poor';
            }
            if (this.corrosionRate > severe) {
                return 'severe';
            }
            return '';
        },
        datePickerLocales() {
            return _datePickerLocales;
        },
    },
    async created() {
        this.mutableAnalysisRequest = { ...this.analysisRequest, sample: this.analysisRequest.samples.find(sample => sample.id === this.sampleId) };
        this.data = await this.prepareEditionData(this.mutableAnalysisRequest.sample);
    },
    methods: {
        async onFileChange(analysisRequest, sampleId, field, value) {
            if (value) {
                this.isLoading = true;
                this.updateData(value);
                await this.updateSampleFile(analysisRequest, sampleId, field, value);
                this.isLoading = false;
            }
        },
        onClearClick(field) {
            this.$rq.deleteSamplePicture(this.analysisRequest.id, this.mutableAnalysisRequest.sample.id, field);
        },
        getCoupon(id) {
            return this.couponList.find(coupon => coupon.id === id);
        },
    },
};
</script>

<style lang="scss">
  .excellent {
    background-color: #2A750F;
  }

  .veryGood {
    background-color:#64A44D;
  }

  .good {
    background-color: #B1C243;
  }

  .fair {
    background-color: #D1A918;
  }

  .poor {
    background-color: #EF4135;
  }

  .severe {
    background-color: #AD1C3F;
  }

.corrosion-bar {
  width: 100%;
  text-align: center;
  margin-bottom: 4px;
  color: #fff;
  margin-top: 4px;
  text-transform: capitalize;
}

::v-deep .v-text-field--filled:not(.v-text-field--single-line) input {
  margin-top: 8px;
}
</style>
